import React, { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { ThemeProvider } from '@material-ui/core/styles';
import { theme } from '../../../config/theme';
import Typography from '@material-ui/core/Typography';
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';
import DownArrow from '../../../images/down_arrow.png';
import { numberAsCurrency } from '../../utils/index';
import moment from 'moment';

const useStyles = makeStyles(() => ({
  root: {
    marginTop: '20px',
  },
  sectionTitle: {
    fontWeight: 700,
    color: '#2D6E2F',
    fontSize: '0.95rem',
    marginBottom: '12px',
  },
  card: {
    marginTop: '12px',
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E7F0',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    padding: '14px',
    textAlign: 'left',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '6px',
    marginBottom: '8px',
  },
  cardTitleRow: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
  },
  medalIcon: {
    fontSize: '1.2rem',
    color: '#C9A227',
    flexShrink: 0,
  },
  cardTitle: {
    fontWeight: 700,
    color: '#1A1A1A',
  },
  soldOutBadge: {
    backgroundColor: '#F8D7DA',
    color: '#721C24',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '0.72rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  totalBadge: {
    backgroundColor: '#D4EDDA',
    color: '#155724',
    borderRadius: '12px',
    padding: '2px 8px',
    fontSize: '0.72rem',
    fontWeight: 700,
    flexShrink: 0,
  },
  meta: {
    fontSize: '0.82rem',
    color: '#5C5C5C',
    marginBottom: '10px',
    lineHeight: 1.5,
  },
  metaStrong: {
    fontWeight: 700,
    color: '#1A1A1A',
  },
  stats: {
    display: 'flex',
    alignItems: 'stretch',
    marginBottom: '12px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E0E4EB',
    borderRadius: '8px',
    overflow: 'hidden',
  },
  statHalf: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '10px 12px',
    fontSize: '0.875rem',
    boxSizing: 'border-box',
  },
  statHalfLeft: {
    borderRight: '1px solid #E0E4EB',
  },
  statLabelGreen: {
    fontWeight: 700,
    color: '#2E7D32',
    marginRight: '6px',
  },
  statLabelBlue: {
    fontWeight: 700,
    color: '#3B7E3D',
    marginRight: '6px',
  },
  statValue: {
    fontWeight: 400,
    color: '#9E9E9E',
  },
  filters: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%',
    maxWidth: '420px',
    margin: '4px auto 20px',
    border: '1px solid #B8D2B9',
    borderRadius: '6px',
    overflow: 'hidden',
    marginBottom: '20px',
    marginTop: '4px',
    '& button': {
      flex: 1,
      border: 0,
      backgroundColor: '#FFFFFF',
      color: '#1A1A1A',
      padding: '8px 12px',
      cursor: 'pointer',
      borderRadius: 0,
      fontWeight: 600,
    },
    '& button:first-child': {
      borderRight: '1px solid #B8D2B9',
    },
  },
  selected: {
    color: 'white !important',
    borderColor: '#3B7E3D !important',
    backgroundColor: '#3B7E3D !important',
  },
  numbers: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(62px, 1fr))',
    gap: '6px',
    '& span': {
      padding: '4px 6px',
      borderRadius: '6px',
      fontSize: '12px',
      textAlign: 'center',
    },
  },
  won: {
    backgroundColor: '#D8CCFF',
    color: '#4A2A90',
  },
  available: {
    backgroundColor: '#A9EAA5',
    color: '#155724',
  },
  arrow: {
    marginLeft: '10px',
    verticalAlign: 'sub',
  },
}));

function SubcampaignCard({ sub }) {
  const classes = useStyles();
  const [tab, setTab] = useState('numbers');
  const canShowWinnersTab = !!sub.show_winners;

  const instant_prizes = sub.instant_prizes || [];
  const instant_winners = sub.instant_winners || [];
  const numberOfDigits = sub.number_of_digits || 1;

  const groupedNumbers = instant_prizes.reduce((acc, prize) => {
    const value = prize.prize_value;
    if (!acc[value]) acc[value] = [];
    acc[value] = acc[value].concat(prize.prize_ticket_numbers).flat().sort((a, b) => a - b);
    return acc;
  }, {});

  function formatValue(value) {
    if (value >= 1000) {
      return (value / 1000).toFixed(1).replace('.0', '') + ' mil';
    }
    return parseFloat(value).toString();
  }

  function padNumber(number) {
    return number.toString().padStart(numberOfDigits, '0');
  }

  function deadline() {
    const time = sub.draw_time || sub.end_time;
    return time ? moment(time).format('DD/MM/YYYY [às] HH:mm') : null;
  }

  const hasInstantPrizes = instant_prizes.length > 0;
  const available = Math.max((sub.instant_prize_tickets || 0) - instant_winners.length, 0);

  return (
    <div className={classes.card}>
      <div className={classes.cardHeader}>
        <span className={classes.cardTitleRow}>
          <EmojiEventsIcon className={classes.medalIcon} />
          <Typography variant="h6" component="span" className={classes.cardTitle}>
            {sub.title}
          </Typography>
        </span>
        {sub.sold_out
          ? <span className={classes.soldOutBadge}>Esgotada</span>
          : hasInstantPrizes && <span className={classes.totalBadge}>Total: {sub.instant_prize_tickets || 0}</span>
        }
      </div>

      <div className={classes.meta}>
        {sub.prize_description
          ? <span>Prêmio principal: <span className={classes.metaStrong}>{sub.prize_description}{sub.prize_value ? ` — ${numberAsCurrency(sub.prize_value)}` : ''}</span></span>
          : <span className={classes.metaStrong}>Somente prêmios instantâneos</span>
        }
        {deadline() && (
          <span> &middot; Prazo: <span className={classes.metaStrong}>{deadline()}</span></span>
        )}
      </div>

      {hasInstantPrizes && (
        <>
          <div className={classes.stats}>
            <div className={`${classes.statHalf} ${classes.statHalfLeft}`}>
              <span className={classes.statLabelGreen}>Disponíveis:</span>
              <span className={classes.statValue}>{available}</span>
            </div>
            <div className={classes.statHalf}>
              <span className={classes.statLabelBlue}>Sorteados:</span>
              <span className={classes.statValue}>{instant_winners.length}</span>
            </div>
          </div>

          <div className={classes.filters}>
            <button onClick={() => setTab('numbers')} className={tab === 'numbers' ? classes.selected : null}>
              Números
            </button>
            {canShowWinnersTab && (
              <button onClick={() => setTab('winners')} className={tab === 'winners' ? classes.selected : null}>
                Ganhadores
              </button>
            )}
          </div>

          {tab === 'numbers' ? (
            <div>
              <span style={{ color: '#3A5D3B', fontSize: '0.82rem' }}>Números premiados:</span>
              <br />
              {Object.keys(groupedNumbers).map((value, index) => (
                <div key={index}>
                  <Typography variant="subtitle1" style={{ fontWeight: 700, color: '#1A1A1A' }}>
                    Prêmio de <span style={{ color: '#2E7D32' }}>R$ {formatValue(value)}</span>
                    <img src={DownArrow} alt="seta para baixo" className={classes.arrow} width="15" height="20" />
                  </Typography>
                  <br />
                  <div className={classes.numbers}>
                    {groupedNumbers[value].map((number, i) => (
                      <span
                        key={i}
                        className={instant_winners.some((w) => w.number == number) ? classes.won : classes.available}
                      >
                        {padNumber(number)}
                      </span>
                    ))}
                  </div>
                  <br />
                </div>
              ))}
            </div>
          ) : (
            <div>
              {instant_winners.map((winner, i) => (
                <div key={i} style={{ padding: '8px 0', borderTop: i === 0 ? 'none' : '1px solid #EEF1F6', fontSize: '0.875rem', color: '#1A1A1A' }}>
                  <strong>{winner.customer?.name}</strong> &mdash; bilhete <strong>{winner.pretty_number}</strong>
                  {winner.instant_prize_value && <span> &middot; {numberAsCurrency(winner.instant_prize_value)}</span>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SubcampaignsSection({ campaign }) {
  const classes = useStyles();
  const details = campaign?.subcampaign_details;
  if (!details || details.length === 0) return null;

  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <div className={classes.sectionTitle}>Campanhas de incentivo</div>
        {details.map((sub) => (
          <SubcampaignCard key={sub.id} sub={sub} />
        ))}
      </div>
    </ThemeProvider>
  );
}
